import { EmptyState } from '../components/ui/EmptyState/EmptyState';
import { AppLayout } from '../layout/AppLayout';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../hooks/redux';
import { Plus, UserGroup } from '../icons/SvgIcons';
import Loader from '../components/ui/Loader/Loader';
import { useAiSystemOverview } from '../components/hooks/useAiSystemOverview';
import Modal from '../components/ui/Modal/Modal';
import { InviteUser } from '../components/UserManagement/InviteUser';
import { useUserManagement } from '../components/hooks/useUserManagement';
import { UserOverview } from '../components/features/UserManagement/UserOverview/UserOverview';
import { useDispatch } from 'react-redux';
import { Toast } from '../components/ui/Toast/Toast';
import { useAccountDefault } from '../components/hooks/useAccountDefault';
import {
  fetchDeleteUserFromAccountSuccess,
  setCurrentUser,
  setIsUserInvited
} from '../store/slices/userManagementSlice';
import { RegisteredUserProfile } from './RegisteredUserProfile';
import { AssignUser } from '../components/UserManagement/AssignUser';
import { inviteUser } from '../services/TeamService';
import { getApiErrorMessage, isApiErrorCode } from '../utils/apiError';
import { featureFlags, appConfig } from '../config/featureFlags';
import {
  selectIsSuperAdmin,
  selectIsUserRole
} from '../store/selectors/authSelectors';

/**
 * The SHG-admin button has no fields to attach inline errors to, so it maps the
 * documented failure codes to a readable sentence for a toast. Anything else
 * falls back to the API message.
 */
function resolveShgInviteError(error: unknown): string {
  if (isApiErrorCode(error, 'PLATFORM_INVITATION_DUPLICATE_PENDING')) {
    return 'An SHG Admin invitation is already pending.';
  }
  if (isApiErrorCode(error, 'USER_ALREADY_IN_ORGANIZATION')) {
    return 'That SHG Admin already has an account.';
  }
  return getApiErrorMessage(
    error,
    'Failed to invite the SHG Admin. Please try again.'
  );
}

export function UserManagementPage() {
  const dispatch = useDispatch();
  const profileHistoryPushedRef = useRef(false);
  const [showToast, setShowToast] = useState(false);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [assignUserModalOpen, setAssignUserModalOpen] = useState(false);
  const [shgAdminInviteToast, setShgAdminInviteToast] = useState(false);
  const [shgAdminInviteError, setShgAdminInviteError] = useState('');
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);
  const [invitingSHGAdmin, setInvitingSHGAdmin] = useState(false);

  const { total, loading } = useAppSelector((state) => state.aiSystems);

  const { account, accountLoading } = useAppSelector(
    (state) => state.accountDefault
  );

  const isSuperAdminRole = useAppSelector(selectIsSuperAdmin);
  const isUserRole = useAppSelector(selectIsUserRole);

  const isPrivateCloud = featureFlags.isPrivateCloud;
  const shgAdminEmail = appConfig.shgAdminEmail;

  const {
    users,
    usersError,
    usersFilters,
    usersPagination,
    teamUsersLoading,
    isUserInvited,
    invitingUser,
    currentUser,
    deleteUserFromAccountSuccess,
    deleteUserFromAccountLoading,
    removedLastSystemPerUserSuccess,
    removedLastSystemPerUserLoading,
    teamRoles,
    teamRolesLoading,
    sorting
  } = useAppSelector((state) => state.userManagement);

  const { fetchSystems } = useAiSystemOverview();
  const { fetchUsers, fetchTeamRoles, fetchTeamUsers } = useUserManagement();
  const { fetchMyAccount } = useAccountDefault();

  // The user directory is token-scoped, so no accountId is needed. The
  // assignable-users list still is, and a superadmin has no account for it.
  const fetchTeamData = useCallback(
    async (accountId?: string): Promise<void> => {
      const requests = [fetchUsers()];

      if (accountId && !isSuperAdminRole) {
        requests.push(fetchTeamUsers(accountId));
      }

      await Promise.all(requests);
    },
    [fetchUsers, fetchTeamUsers, isSuperAdminRole]
  );

  function handleAddUser() {
    setInviteUserModalOpen(true);
  }

  useEffect(() => {
    let isMounted = true;

    const loadInitialUserManagementData = async () => {
      setIsInitialDataLoading(true);
      setShowToast(false);
      dispatch(setCurrentUser(null));

      try {
        const [currentAccount] = await Promise.all([
          fetchMyAccount(),
          fetchTeamRoles()
        ]);

        if (!isMounted) {
          return;
        }

        // The user directory is fetched by the effect below once this flag
        // clears — doing it here too would just double the request. The AI
        // systems list is scoped server-side, so a user gets exactly the systems
        // they are assigned to without us narrowing by owner.
        const requests = [fetchSystems(false)];

        if (currentAccount?.id && !isSuperAdminRole) {
          requests.push(fetchTeamUsers(currentAccount.id));
        }

        await Promise.all(requests);
      } catch (error) {
        console.error('Failed to bootstrap user management:', error);
      } finally {
        if (isMounted) {
          setIsInitialDataLoading(false);
        }
      }
    };

    loadInitialUserManagementData();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  // Sorting, searching, filtering and paging are all server-side now, so any
  // change to them is a refetch. A non-zero offset is load-more, which appends
  // rather than replacing; every other change resets the offset to 0.
  useEffect(() => {
    if (isInitialDataLoading) return;

    fetchUsers(usersPagination.offset > 0);
  }, [
    isInitialDataLoading,
    sorting,
    usersFilters,
    usersPagination.offset,
    usersPagination.limit
  ]);

  useEffect(() => {
    if (isUserInvited) {
      fetchTeamData(account?.id);
      setShowToast(true);
      dispatch(setIsUserInvited(false));
    }
  }, [isUserInvited]);

  useEffect(() => {
    if (deleteUserFromAccountSuccess) {
      fetchTeamData(account?.id);
      // The success toast moved into RegisteredUserProfile.confirmDelete: only
      // there is the row type still known, and by the time this effect runs
      // currentUser is already null, so a deleted member and a revoked
      // invitation would have to share one wrong message.
      dispatch(fetchDeleteUserFromAccountSuccess(false));
    }
  }, [deleteUserFromAccountSuccess]);

  useEffect(() => {
    if (removedLastSystemPerUserSuccess) {
      fetchTeamData(account?.id);
    }
  }, [removedLastSystemPerUserSuccess]);

  useEffect(() => {
    if (!currentUser) {
      profileHistoryPushedRef.current = false;
      return;
    }

    if (!profileHistoryPushedRef.current) {
      window.history.pushState(
        { userManagementProfileOpen: true },
        '',
        window.location.href
      );
      profileHistoryPushedRef.current = true;
    }

    const handlePopState = () => {
      dispatch(setCurrentUser(null));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentUser, dispatch]);

  const hasSystems = total && total > 0;
  const hasUsers = (users?.total ?? 0) > 0;
  // With server-side search and filtering, an empty result is not an empty
  // directory — the controls must stay on screen so they can be cleared.
  const hasActiveUserCriteria =
    usersFilters.q.length > 0 ||
    usersFilters.type !== 'ALL' ||
    usersFilters.organizationId.length > 0;
  const hasNoPlan = account?.subscription?.plan === 'NO_PLAN';
  const canInviteUser = !isUserRole;
  const canAssignUser = !isSuperAdminRole && hasSystems;

  // The first page of users has not arrived yet. Distinct from usersLoading,
  // which is deliberately excluded below: that flips on every search keystroke
  // and filter change, and swapping the whole page for a spinner would unmount
  // the search box mid-typing. Without this the "No Users yet" empty state
  // flashes on every page load, before the first response lands.
  const awaitingFirstUsersPage = users === null && !usersError;

  const isUserManagementLoading =
    isInitialDataLoading ||
    awaitingFirstUsersPage ||
    loading ||
    accountLoading ||
    teamUsersLoading ||
    teamRolesLoading ||
    invitingUser ||
    deleteUserFromAccountLoading ||
    removedLastSystemPerUserLoading;

  const showEmptyState =
    !isUserManagementLoading &&
    !currentUser &&
    !hasUsers &&
    !hasActiveUserCriteria;

  const showUserOverview =
    !isUserManagementLoading &&
    !currentUser &&
    (hasUsers || hasActiveUserCriteria);

  const handleSeccondButton = async () => {
    // check super admin role and private cloud
    if (isSuperAdminRole && isPrivateCloud) {
      // No fields to render an inline error on, so a missing REACT_APP_SHG_ADMIN_EMAIL
      // would otherwise surface only as an opaque 400. Guard it before the request.
      if (!shgAdminEmail) {
        setShgAdminInviteError(
          'SHG Admin email is not configured. Set REACT_APP_SHG_ADMIN_EMAIL and reload.'
        );
        return;
      }

      setInvitingSHGAdmin(true);
      try {
        // Grants the SHG address an org-less SUPER_ADMIN (platform-staff) account,
        // not the founding admin of a new organization.
        await inviteUser({
          email: shgAdminEmail,
          role: 'SUPER_ADMIN'
        });
        setShgAdminInviteToast(true);
      } catch (error) {
        // inviteUser sets skipErrorToast, so surface the failure here instead of
        // leaving the button inert.
        setShgAdminInviteError(resolveShgInviteError(error));
      } finally {
        setInvitingSHGAdmin(false);
      }
      return;
    }

    setAssignUserModalOpen(true);
  };

  // check super admin and private cloud
  const seccondButtonText =
    isSuperAdminRole && isPrivateCloud
      ? 'Invite SHG Admin'
      : canAssignUser
        ? 'Assign user'
        : undefined;

  const secconButtonDisabled = isSuperAdminRole ? false : !canAssignUser;

  return (
    <AppLayout>
      <DashboardLayout
        title="User Management"
        addButtonText={canInviteUser ? 'Invite User' : undefined}
        onAddButtonClick={handleAddUser}
        addButtonDisabled={!canInviteUser}
        secondButtonText={seccondButtonText}
        secondButtonOnClick={handleSeccondButton}
        secondButtonDisabled={secconButtonDisabled || invitingSHGAdmin}
      >
        {isUserManagementLoading && (
          <div className="absolute top-1/2 left-1/2">
            <Loader />
          </div>
        )}

        {currentUser != null && <RegisteredUserProfile />}

        {showEmptyState && (
          <EmptyState
            icon={<UserGroup />}
            title={'No Users yet'}
            description={
              canInviteUser
                ? 'Add your first user to begin managing access.'
                : hasNoPlan
                  ? 'User management is unavailable without an active subscription plan.'
                  : 'User management is unavailable until at least one AI system exists.'
            }
            buttonIcon={canInviteUser ? <Plus /> : undefined}
            buttonText={canInviteUser ? 'Invite User' : undefined}
            onButtonClick={canInviteUser ? handleAddUser : undefined}
          />
        )}
        {showUserOverview && <UserOverview />}

        {showToast && (
          <Toast
            text="User successfully invited!"
            showToast={showToast}
            setShowToast={setShowToast}
          />
        )}
        {shgAdminInviteToast && (
          <Toast
            text="SHG Admin successfully invited!"
            showToast={shgAdminInviteToast}
            setShowToast={setShgAdminInviteToast}
          />
        )}
        {shgAdminInviteError && (
          <Toast
            text={shgAdminInviteError}
            type="error"
            showToast={!!shgAdminInviteError}
            setShowToast={() => setShgAdminInviteError('')}
          />
        )}

        <Modal
          open={inviteUserModalOpen}
          setOpen={setInviteUserModalOpen}
          dialogPanelClassnames="min-w-[820px] max-h-[calc(100vh_-_70px)] overflow-y-auto"
        >
          <InviteUser
            setIsOpen={setInviteUserModalOpen}
            userRoles={teamRoles?.platformRoles || []}
            showOrgFields={isSuperAdminRole}
          />
        </Modal>
        <Modal
          open={assignUserModalOpen}
          setOpen={setAssignUserModalOpen}
          dialogPanelClassnames="min-w-[820px] max-h-[calc(100vh_-_70px)] overflow-y-auto"
        >
          <AssignUser
            setIsOpen={setAssignUserModalOpen}
            userRoles={teamRoles?.aiSystemRoles || []}
          />
        </Modal>
      </DashboardLayout>
    </AppLayout>
  );
}
